import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import { getResidentReports } from "../../../Redux/Reports/ReportsActions";
import Loading from "../../../Components/Common/Loading/Loading";
import { AppDispatch, RootState } from "../../../Redux/store";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import exportToExcel from "../../../Util/exportToExcel";
import { useDispatch, useSelector } from "react-redux";
import styles from "./ResidentReports.module.scss";
import React, { useEffect, useState } from "react";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";

interface ResidentReportsType {
  building: string;
  leaseUnit: number;
  totalNonRegisteredResidents: number;
  totalNonRegisteredResidentsPercentage: number;
  totalRegisteredResidents: number;
  totalRegisteredResidentsPercentage: number;
  totalResidents: number;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

const columnDefs: ColDef<ResidentReportsType>[] = [
  {
    headerName: "Building",
    field: "building",
    minWidth: 150,
    flex: 1,
  },
  {
    headerName: "Number Unit",
    field: "leaseUnit",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Total Residents",
    field: "totalResidents",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Total Residents with Membership",
    field: "totalRegisteredResidents",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Total Residents without Membership",
    field: "totalNonRegisteredResidents",
    minWidth: 150,
    flex: 1,
  },

  {
    headerName: "Percentage of Residents Members",
    field: "totalRegisteredResidentsPercentage",
    minWidth: 150,
    flex: 1,
  },
  {
    headerName: "Percentage of Residents Non Members",
    field: "totalNonRegisteredResidentsPercentage",
    minWidth: 150,
    flex: 1,
  },
];

function ResidentReports() {
  const [rowData, setRowData] = useState<ResidentReportsType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [buildingFilter, setBuildingFilter] = useState<string>("");
  const dispatch = useDispatch<AppDispatch>();
  const { residentReports, residentReportsLoading } = useSelector(
    (state: RootState) => state.reports,
  );
  const { buildings } = useSelector((state: RootState) => state.buildings);
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const applyFilters = (rowData: ResidentReportsType[]) => {
    let array = [...rowData];

    if (search?.length > 3) {
      array = array.filter(
        (member: ResidentReportsType) =>
          member?.building?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.leaseUnit
            ?.toString()
            ?.toLowerCase()
            ?.includes(search?.toLowerCase()),
      );
    }

    if (buildingFilter) {
      array = array.filter(
        (member: ResidentReportsType) => member?.building === buildingFilter,
      );
    }

    return array;
  };

  const filteredRowData = applyFilters(rowData);

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getResidentReports());
    dispatch(getAllBuildings());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (residentReports) {
      setRowData(residentReports);
    }
  }, [residentReports]);

  if (residentReportsLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.topContainer}>
        <div className={styles.headingContainer}>
          <h1 className={styles.heading}>Resident Reports</h1>
        </div>
        <div className={styles.buttonContainer}>
          {filteredRowData?.length > 0 && (
            <button
              className={styles.button}
              onClick={() =>
                exportToExcel({
                  data: filteredRowData || [],
                  fileName: "Resident Reports",
                })
              }
            >
              Export
            </button>
          )}
        </div>
      </div>
      <div className={styles.bodyContainer}>
        <div className={styles.doubleInputContainer}>
          <div className={styles.searchContainer}>
            <div className={styles.searchContainerInner}>
              <input
                type="text"
                className={styles.input}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e?.target?.value || "")}
              />
              <div
                className={styles.iconContainer}
                onClick={() => setSearch("")}
              >
                <IoCloseSharp className={styles.icon} />
              </div>
            </div>
          </div>
          <div className={styles.filtersContainer}>
            <div className={styles.filterItem}>
              <p className={styles.label}>Building</p>
              <select
                className={styles.input}
                value={buildingFilter}
                onChange={(e) => setBuildingFilter(e?.target?.value || "")}
              >
                <option value="">All</option>
                {buildings?.map((building: any, i: number) => {
                  return (
                    <option key={i} value={building?.building}>
                      {building?.buildingName}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
        <div className={styles.gridContainer}>
          <AgGridReact
            className={styles.grid}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            animateRows={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
    </div>
  );
}

export default ResidentReports;
